"use client";

import { Alert, Box, Button, HStack, Stack, Text } from "@chakra-ui/react";
import type { FileChangeDetails } from "@zag-js/file-upload";
import { useCallback, useMemo, useState } from "react";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericUploadFile from "@/shared/ui/GenericUploadFile";
import {
  formatAudioDurationLimit,
  MAX_AUDIO_UPLOAD_BYTES,
  MAX_AUDIO_UPLOAD_DURATION_SECONDS,
  SUPPORTED_AUDIO_UPLOAD_ACCEPT,
  SUPPORTED_AUDIO_UPLOAD_DISPLAY,
  validateAudioUploadFile,
} from "../utils/audioUpload";

interface AudioFilePickerProps {
  canTranscribeAndSubmit?: boolean;
  isTranscribing?: boolean;
  transcribeAndSubmitDisabledReason?: string;
  transcribeAndSubmitLabel?: string;
  transcribeAndSubmitLoadingText?: string;
  onReset?: () => void;
  onTranscribe: (selectedFile: File) => Promise<void> | void;
  onTranscribeAndSubmit?: (selectedFile: File) => Promise<void> | void;
}

export default function AudioFilePicker({
  canTranscribeAndSubmit = true,
  isTranscribing = false,
  transcribeAndSubmitDisabledReason,
  transcribeAndSubmitLabel = "Transcribe & submit",
  transcribeAndSubmitLoadingText = "Submitting",
  onReset,
  onTranscribe,
  onTranscribeAndSubmit,
}: AudioFilePickerProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );
  const canUseTranscribeAndSubmit =
    Boolean(selectedFile) &&
    !isTranscribing &&
    !validationMessage &&
    canTranscribeAndSubmit &&
    Boolean(onTranscribeAndSubmit);

  const maxFileSizeMb = useMemo(
    () => Math.round(MAX_AUDIO_UPLOAD_BYTES / (1024 * 1024)),
    [],
  );
  const maxDurationLabel = useMemo(
    () => formatAudioDurationLimit(MAX_AUDIO_UPLOAD_DURATION_SECONDS),
    [],
  );

  const clearSelectedFile = useCallback(() => {
    setSelectedFile(null);
    setValidationMessage(null);
    setFileInputKey((current) => current + 1);
    onReset?.();
  }, [onReset]);

  const handleFileChange = useCallback(
    (details: FileChangeDetails) => {
      const nextFile = details.acceptedFiles[0];

      if (!nextFile) {
        clearSelectedFile();
        return;
      }

      const validationError = validateAudioUploadFile(nextFile);
      if (validationError) {
        setValidationMessage(validationError);
        setSelectedFile(null);
        setFileInputKey((current) => current + 1);
        onReset?.();
        return;
      }

      setValidationMessage(null);
      setSelectedFile(nextFile);
      onReset?.();
    },
    [clearSelectedFile, onReset],
  );

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      width="full"
      p={{ base: "3", md: "4" }}
      bg="moveon.surface.elevatedBg"
    >
      <HStack gap="2" align="center" mb="3">
        <GenericIcon icon="upload" humanReadable="Audio upload" />
        <Text fontWeight="medium">Audio file</Text>
      </HStack>

      <GenericUploadFile
        accept={SUPPORTED_AUDIO_UPLOAD_ACCEPT}
        fileInputKey={fileInputKey}
        helperText={
          <>
            Supported formats: {SUPPORTED_AUDIO_UPLOAD_DISPLAY}. Max size:{" "}
            {maxFileSizeMb}MB. Max length: {maxDurationLabel}.
          </>
        }
        label="Audio file"
        selectedFile={selectedFile}
        validationMessage={validationMessage}
        onClear={clearSelectedFile}
        onFileChange={handleFileChange}
      />

      <Stack
        mt="4"
        direction={{ base: "column", sm: "row" }}
        gap={{ base: "2", sm: "3" }}
        align={{ base: "stretch", sm: "center" }}
        wrap={{ base: "nowrap", sm: "wrap" }}
      >
        <Button
          aria-label="Transcribe selected source audio file"
          title="Transcribe selected source audio file"
          bg="moveon.primary"
          onClick={() => selectedFile && onTranscribe(selectedFile)}
          disabled={!selectedFile || isTranscribing || !!validationMessage}
          loading={isTranscribing}
          loadingText="Transcribing"
          width={{ base: "full", sm: "auto" }}
          color="white"
        >
          <GenericIcon icon="transcribe" humanReadable="Transcribe" />
          <Box as="span">Transcribe</Box>
        </Button>

        {onTranscribeAndSubmit ? (
          <Button
            aria-label={transcribeAndSubmitLabel}
            title={transcribeAndSubmitLabel}
            variant="outline"
            onClick={() =>
              selectedFile && onTranscribeAndSubmit(selectedFile)
            }
            disabled={!canUseTranscribeAndSubmit}
            loading={isTranscribing}
            loadingText={transcribeAndSubmitLoadingText}
            width={{ base: "full", sm: "auto" }}
          >
            <GenericIcon
              icon="transcribe"
              humanReadable={transcribeAndSubmitLabel}
            />
            <Box as="span">{transcribeAndSubmitLabel}</Box>
          </Button>
        ) : null}

        <Button
          aria-label="Clear selected source audio file"
          title="Clear selected source audio file"
          variant="outline"
          color="moveon.warning"
          borderColor="moveon.warningBorder"
          onClick={clearSelectedFile}
          disabled={!selectedFile || isTranscribing}
          width={{ base: "full", sm: "auto" }}
          _hover={{
            bg: "moveon.warningLight",
          }}
        >
          <GenericIcon icon="reset" humanReadable="Clear selected audio" />
          <Box as="span">Clear</Box>
        </Button>
      </Stack>

      {onTranscribeAndSubmit && transcribeAndSubmitDisabledReason ? (
        <Box color="moveon.text.muted" fontSize="xs" mt="2">
          {transcribeAndSubmitDisabledReason}
        </Box>
      ) : null}

      {selectedFile && !validationMessage && (
        <Alert.Root mt="4" status="info" borderRadius="lg">
          <Alert.Indicator />
          <Alert.Description>
            File is ready for transcription. You can retry with the same file if
            transcription fails.
          </Alert.Description>
        </Alert.Root>
      )}
    </Box>
  );
}
